import { useState, useEffect } from "react";
import axiosInstance from "../utils/axiosConfig";
import Grid from "@material-ui/core/Grid";
import Divider from "@material-ui/core/Divider";
import { makeStyles } from "@material-ui/core/styles";
import Modal from "@material-ui/core/Modal";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Button from "../Input/Button";
import CountriesMap from "../Profile/CountriesMap";


export default function PersonalInfoModal(props) {
    const { theme, open, handleClose } = props;
    
    // Get the selected child ID from localStorage
    const selectedChildId = window.localStorage.getItem('SELECTED_CHILD_ID');
    
    const [formData, setFormData] = useState({
        name: "",
        displayName: "",
        nationality: ""
    });
    
    const [loading, setLoading] = useState(false);
    const [childData, setChildData] = useState(null);
    
    // Fetch the child data when the modal opens
    useEffect(() => {
    if (open && selectedChildId) {
        fetchChildData();
    }
    }, [open, selectedChildId]);
    
    const fetchChildData = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get(`/user/${selectedChildId}`);
            
            if (response.data) {
                setChildData(response.data);
                setFormData({
                    name: response.data.name || "",
                    displayName: response.data.display_name || response.data.name || "",
                    nationality: response.data.nationality || ""
                });
            }
        } catch (error) {
            console.error("Error fetching child data:", error);
        } finally {
            setLoading(false);
        }
    };
    
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };
    
    const handleSubmit = async () => {
        const currentChildSettings = JSON.parse(window.localStorage.getItem('SELECTED_CHILD_SETTINGS') || '{}');
        if (!selectedChildId) return;
        
        try {
            setLoading(true);
            
            // Update the nationality
            const updateNationalityResponse = await axiosInstance.put(
                `/user/${selectedChildId}/nationality`, 
                {
                    new_nationality: formData.nationality,
                    display_name: formData.displayName
                }
            );
            
            // If the nationality update was successful, update the localStorage
            if (updateNationalityResponse.status === 200) {
                window.localStorage.setItem('SELECTED_CHILD_SETTINGS', JSON.stringify({
                    ...currentChildSettings,
                    nationality: formData.nationality
                }));
            }
            
            // Update the name
            const updateNameResponse = await axiosInstance.put(
                `/user/${selectedChildId}/name`, 
                {
                    new_name: formData.name
                }
            )
            
            // If the name update was successful, update the localStorage
            if (updateNameResponse.status === 200) {
                window.localStorage.setItem('SELECTED_CHILD_SETTINGS', JSON.stringify({
                    ...currentChildSettings,
                    name: formData.name
                }));
            }
            
            // Close the modal
            handleClose();
            
            // Refresh the page to show updated information
            window.location.reload();
        } catch (error) {
            console.error("Error updating child information:", error);
        } finally {
            setLoading(false);
        }
    };
    
    const useStyles = makeStyles((theme) => ({
        modal: {
            position: "absolute",
            width: 500,
            backgroundColor: theme.palette.background.paper,
            border: "2px solid #000",
            boxShadow: theme.shadows[5],
            padding: theme.spacing(2, 4, 3),
            top: "30%",
            left: "50%",
            transform: "translate(-50%, -30%)",
            maxHeight: "80vh",
            overflow: "auto"
        },
        title: {
            fontSize: 24,
            fontWeight: "bold",
            marginBottom: theme.spacing(2),
            color: theme.primaryColor,
            textAlign: "center"
        },
        formControl: {
            margin: theme.spacing(1),
            width: "100%"
        },
        buttonContainer: {
            display: "flex",
            justifyContent: "center",
            marginTop: theme.spacing(3)
        },
        divider: {
            margin: theme.spacing(2, 0)
        }
    }));
    
    const classes = useStyles();
    
    return (
        <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="personal-info-modal"
            aria-describedby="edit-child-personal-information"
        >
            <div className={classes.modal}>
                <Typography variant="h5" className={classes.title}>
                    Edit Personal Information
                </Typography>
                
                {loading && !childData ? (
                <Typography>Loading child information...</Typography>
                ) : (
                <>
                    <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <FormControl className={classes.formControl}>
                        <TextField
                            label="Name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            variant="outlined"
                            fullWidth
                        />
                        </FormControl>
                    </Grid>
                    
                    <Grid item xs={12}>
                        <FormControl variant="outlined" className={classes.formControl}>
                        <InputLabel id="nationality-select-label">Nationality</InputLabel>
                        <Select
                            labelId="nationality-select-label"
                            id="nationality-select"
                            name="nationality"
                            value={formData.nationality}
                            onChange={handleInputChange}
                            label="Nationality"
                        >
                            <MenuItem value="">
                            <em>Select Nationality</em>
                            </MenuItem>
                            {Object.entries(CountriesMap).map(([code, name]) => (
                            <MenuItem key={code} value={code.toLowerCase()}>
                                {name}
                            </MenuItem>
                            ))}
                        </Select>
                        </FormControl>
                    </Grid>
                    </Grid>
                    
                    <Divider className={classes.divider} />
                    
                    <div className={classes.buttonContainer}>
                    <Button 
                        theme={theme} 
                        onClick={handleSubmit}
                        disabled={loading}
                    >
                        {loading ? "Saving..." : "Save Changes"}
                    </Button>
                    </div>
                </>
                )}
            </div>
        </Modal>
    );
}